// src/pages/Upload.js
import React, { useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import CustomButton from '../components/CustomButton';
import { InlineNotification } from '@carbon/react';
import api from "../api";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// 共通スキーマ（DB定義書のキー名・日本語ラベル）
import { FIELDS, labelOf, SECTION_GROUPS, typeOf } from '../schema/patientSchema';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * ▼ スキーマ（Excel取込用）
 *  - partialDate は文字列としてそのまま許容（年/年月/年月日）
 *  - date は厳密日付（YYYY/MM/DD または Excel日付）を parse
 *  - enum チェックは列挙に一致するか検証
 *  - decimal1 は小数1位
 *  - 必須: 研究ID / 性別 / MSのタイプ / 過去データ1回目の診察日 / 過去データ1回目の薬剤名
 */
const SCHEMA = FIELDS.map((f) => ({
  key: f.key,
  jp: f.jp,
  required:
    f.key === 'research_id' ||
    f.key === 'gender_type' ||
    f.key === 'ms_type' ||
    f.key === 'treatment_start_1' ||
    f.key === 'treatment_drug_1',
  type:
    f.type === 'partialDate'
      ? 'string'
      : f.type === 'password'
      ? 'string'
      : f.type,
  enum: Array.isArray(f.enum) ? f.enum : undefined,
}));

// プレビュー表示順（セクション順 + 過去データ1回目）
const PREVIEW_ORDER = [
  ...SECTION_GROUPS.personal,
  ...SECTION_GROUPS.history,
  ...SECTION_GROUPS.lab,
  'treatment_start_1',
  'treatment_drug_1',
];

// ファイル制限
const ALLOWED_EXTS = ['xlsx', 'xls'];
const ALLOWED_MIMES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
];
const MAX_BYTES = 10 * 1024 * 1024; // 10MB

// 日付判定（YYYY/MM/DD or YYYY-MM-DD or Excel数値シリアル）
const parseDate = (v) => {
  if (v == null || v === '') return { ok: true, value: '' };

  if (v instanceof Date && !isNaN(v)) {
    return { ok: true, value: formatDate(v) };
  }

  const s = String(v).trim();

  if (/^\d+(\.\d+)?$/.test(s)) {
    const d = excelDateToJSDate(Number(s));
    return isValidDate(d) ? { ok: true, value: formatDate(d) } : { ok: false };
  }

  const norm = s.replace(/-/g, '/');
  const d = new Date(norm);

  return isValidDate(d) ? { ok: true, value: formatDate(d) } : { ok: false };
};

const formatDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');

  return `${y}/${m}/${day}`;
};

const isValidDate = (d) => d instanceof Date && !isNaN(d.getTime());

const excelDateToJSDate = (serial) => {
  const utcDays = Math.floor(serial - 25569);
  const utcValue = utcDays * 86400;

  return new Date(utcValue * 1000);
};

// 表示用フォーマッタ
function formatDateLabel(v) {
  if (!v) return '（空）';

  const d = new Date(v);

  if (Number.isNaN(d.getTime())) return '（空）';

  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
}

function formatPartialDateLabel(v) {
  if (!v) return '（空）';

  const s = String(v).trim();

  if (/^\d{4}$/.test(s)) return `${s}年`;

  const m = s.replace(/-/g, '/').match(/^(\d{4})\/(\d{1,2})(?:\/(\d{1,2}))?$/);

  if (m) {
    const y = Number(m[1]);
    const mm = Number(m[2]);
    const dd = m[3] ? Number(m[3]) : null;

    return dd ? `${y}年${mm}月${dd}日` : `${y}年${mm}月`;
  }

  const d = new Date(s);

  if (!Number.isNaN(d.getTime())) {
    return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
  }

  return s || '（空）';
}

export default function Upload() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState([]);
  const [rows, setRows] = useState([]);
  const [rowErrors, setRowErrors] = useState([]);

  const handlePick = () => inputRef.current?.click();

  const getFileErrors = (f) => {
    const errs = [];

    if (!f) {
      errs.push('ファイルが選択されていません。');
      return errs;
    }

    const ext = (f.name.split('.').pop() || '').toLowerCase();

    if (!ALLOWED_EXTS.includes(ext)) {
      errs.push('Excel 形式（.xlsx / .xls）のみアップロードできます。');
    }

    if (f.type && !ALLOWED_MIMES.includes(f.type)) {
      errs.push(`ファイル形式が不正です（${f.type}）。Excel 形式のみ対応です。`);
    }

    if (f.size === 0) {
      errs.push('空のファイルはアップロードできません。');
    }

    if (f.size > MAX_BYTES) {
      errs.push(`ファイルサイズが大きすぎます（最大 ${Math.floor(MAX_BYTES / (1024 * 1024))}MB）。`);
    }

    return errs;
  };

  const handleChange = async (e) => {
    setRows([]);
    setRowErrors([]);
    setFile(null);

    const f = e.target.files?.[0];
    const fe = getFileErrors(f);

    if (fe.length) {
      setErrors(fe);

      if (inputRef.current) inputRef.current.value = '';

      return;
    }

    setErrors([]);
    setFile(f);

    try {
      const x = await import('xlsx');
      const XLSX = x.default || x;
      const data = await f.arrayBuffer();
      const wb = XLSX.read(data, { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];

      // 英語ヘッダ行を強制的に読み込む
      const headers = XLSX.utils.sheet_to_json(sheet, {
        range: 4,
        header: 1,
      })[0];

      console.log("英語ヘッダ:", headers);

      // データ行をヘッダ指定で再取得
      const json = XLSX.utils.sheet_to_json(sheet, {
        range: 6,
        header: headers,
        defval: "",
      });

      console.log("1行目データ:", json[0]);

      if (!json.length) {
        setErrors(['シートにデータがありません。']);
        return;
      }

      const headerMap = buildHeaderMap(Object.keys(json[0]));
      const normalized = json.map((raw) => normalizeRow(raw, headerMap));
      const { validated, issues } = validateRows(normalized);

      setRows(validated);
      setRowErrors(issues);

    } catch (err) {
      console.error(err);
      setErrors(['Excel の解析に失敗しました。ファイル内容をご確認ください。']);
    }
  };

  const handleClear = () => {
    setFile(null);
    setRows([]);
    setRowErrors([]);
    setErrors([]);

    if (inputRef.current) inputRef.current.value = '';
  };

  const handleStartLoad = async () => {
    if (!rows.length) return;

    try {
      for (const r of rows) {
        await registerPatient(r);
        await registerMedicalInfo(r);
      }

      alert("登録が完了しました！");
      navigate("/patient-selection", { replace: true });

    } catch (err) {
      console.error(err);

      const apiMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        err?.message ||
        "登録API呼び出し中にエラーが発生しました。";

      setErrors([apiMessage]);
    }
  };

  /** ヘッダーマップ作成：見出し配列 -> { 見出し名(lower) : 正規キー } */
  const buildHeaderMap = (headers) => {
    const map = {};
    const lower = (s) => String(s).trim().toLowerCase();

    const candidates = {};

    SCHEMA.forEach((s) => {
      candidates[lower(s.key)] = s.key;
      candidates[lower(s.jp)] = s.key;
    });

    const ENGLISH_HEADERS = {
      "patient name": "consultation_id",
      "research id": "research_id",
      "patient password": "patient_pw",
      "date of birth": "date_of_birth",
      "gender": "gender_type",
      "ms type": "ms_type",
      "edss score": "edss_score",
      "hope child": "hope_child",
      "smoking": "smoking",
      "drinking": "drinking",
      "complications": "complications",
      "medical history": "medical_history",
      "family history": "family_history",
      "time of onset": "first_detect_date",
      "onset pattern": "first_detect_way",
      "time of first onset": "first_onset_date",
      "location of first onset": "first_onset_location",
      "first onset symptom": "first_onset_symptom",
      "total number of attacks (including first)": "recurrences_times",
      "time of last onset": "last_onset_date",
      "presence or absence of pira": "pira_presence",
      "when pira was suspected": "pira_suspected_date",
      "symptoms of pira": "pira_symptoms",
      "last edss3 date": "last_edss3_date",
      "last edss6 date": "last_edss6_date",
      "antibody test (anti-aqp4)": "aqp4_test",
      "antibody test (anti-mog)": "mog_test",
      "antibody test (other)": "antibody_test",
      "whether or not cerebrospinal fluid testing is performed": "cerebrospinal_fluid_test",
      "when to perform cerebrospinal fluid testing": "fluid_test_date",
      "ocb of cerebrospinal fluid test": "ocb_result_type",
      "cerebrospinal fluid testing company": "fluid_test_company",
      "igg index": "igg_result_score",
      "start time of treatment 1": "treatment_start_1",
      "start time of treatment 2": "treatment_start_2",
      "start time of treatment 3": "treatment_start_3",
      "start time of treatment 4": "treatment_start_4",
      "start time of treatment 5": "treatment_start_5",
      "start time of treatment 6": "treatment_start_6",
      "start time of treatment 7": "treatment_start_7",
      "start time of treatment 8": "treatment_start_8",
      "start time of treatment 9": "treatment_start_9",
      "start time of treatment 10": "treatment_start_10",
      "treatment drug 1": "treatment_drug_1",
      "treatment drug 2": "treatment_drug_2",
      "treatment drug 3": "treatment_drug_3",
      "treatment drug 4": "treatment_drug_4",
      "treatment drug 5": "treatment_drug_5",
      "treatment drug 6": "treatment_drug_6",
      "treatment drug 7": "treatment_drug_7",
      "treatment drug 8": "treatment_drug_8",
      "treatment drug 9": "treatment_drug_9",
      "treatment drug 10": "treatment_drug_10",
    };

    Object.entries(ENGLISH_HEADERS).forEach(([eng, key]) => {
      candidates[eng] = key;
    });

    headers.forEach((h) => {
      const k = candidates[lower(h)];

      if (k) map[h] = k;
    });

    return map;
  };

  /** 行正規化：ヘッダー写像 + 型変換（date, decimal1, enum, string） */
  const normalizeRow = (raw, headerMap) => {
    const out = {};

    SCHEMA.forEach((s) => {
      out[s.key] = '';
    });

    Object.entries(raw).forEach(([col, val]) => {
      const k = headerMap[col];

      if (!k) return;

      const rule = SCHEMA.find((s) => s.key === k);

      if (!rule) return;

      const v = (val ?? '').toString().trim();

      switch (rule.type) {
        case 'date': {
          const p = parseDate(val);
          out[k] = p.ok ? p.value : v ? `__INVALID__${v}` : '';
          break;
        }

        case 'decimal1': {
          if (v === '') {
            out[k] = '';
            break;
          }

          const n = Number(v);
          out[k] = isFinite(n) ? Number(n.toFixed(1)).toFixed(1) : `__INVALID__${v}`;
          break;
        }

        case 'enum': {
          if (v === '') {
            out[k] = '';
            break;
          }

          out[k] = rule.enum?.includes(v) ? v : `__INVALID__${v}`;
          break;
        }

        default:
          out[k] = v;
      }
    });

    return out;
  };

  /** 行検証：必須・型エラーを収集 */
  const validateRows = (normalizedRows) => {
    const issues = [];

    const validated = normalizedRows
      .map((row, idx) => {
        const allEmpty = Object.values(row).every((v) => !String(v || '').trim());

        if (allEmpty) return null;

        const msgs = [];
        const badFields = [];

        SCHEMA.forEach((rule) => {
          const v = row[rule.key];

          // 必須
          if (rule.required && !String(v || '').trim()) {
            msgs.push(`${labelOf(rule.key)} は必須です`);
            badFields.push(rule.key);
          }

          // 型・列挙の invalid マーク
          if (String(v).startsWith('__INVALID__')) {
            const raw = v.replace('__INVALID__', '');

            const typeMsg =
              rule.type === 'date' ? '日付(YYYY/MM/DD または Excel 日付)' :
              rule.type === 'decimal1' ? '小数1位までの数値' :
              rule.type === 'enum' ? `決められた選択肢（${(rule.enum || []).join(' / ')}）` :
              '文字列';

            msgs.push(`${labelOf(rule.key)} の形式が不正です（${raw} → ${typeMsg}）`);
            badFields.push(rule.key);
            row[rule.key] = raw;
          }
        });

        if (msgs.length) {
          issues.push({
            index: idx,
            messages: msgs,
            fields: badFields,
          });
        }

        return row;
      })
      .filter(Boolean);

    return { validated, issues };
  };

  const allOk = file && errors.length === 0 && rowErrors.length === 0 && rows.length > 0;

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;

    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const formatType = (file) => {
    if (!file) return '不明';

    const name = file.name.toLowerCase();

    if (name.endsWith('.xlsx')) return 'Excelファイル（.xlsx）';
    if (name.endsWith('.xls')) return 'Excelファイル（.xls）';
    if (name.endsWith('.csv')) return 'CSVファイル（.csv）';
    if (name.endsWith('.json')) return 'JSONファイル（.json）';

    return '不明';
  };

  return (
    <>
      <div style={{ minHeight: '100vh', background: '#f4f4f4' }}>
        <div style={{ maxWidth: 960, margin: '0 auto', padding: '1rem', textAlign: 'center' }}>
          <div style={{ marginBottom: '0.5rem', textAlign: 'left' }}>
            <Link
              to="/patient-registration"
              className="sidebar-link"
              style={{ textDecoration: 'none', color: '#0062ff', fontWeight: 'bold' }}
            >
              ← 登録方法選択に戻る
            </Link>
          </div>

          {errors.length > 0 && (
            <div
              style={{
                marginBottom: '1rem',
                background: '#fff',
                border: '1px solid #fa4d56',
                borderRadius: 6,
                color: '#750e13',
                padding: '0.75rem 1rem',
              }}
              aria-live="assertive"
            >
              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                アップロードできません
              </div>
              {errors.map((e, i) => (
                <div key={i}>・{e}</div>
              ))}
            </div>
          )}

          <h2 style={{ margin: '1rem 0' }}>新規患者の登録（ファイル）</h2>

          <CustomButton
            kind="primary"
            onClick={handlePick}
            className="btn-wide"
            style={{ maxWidth: 240, margin: '0 auto' }}
          >
            ファイル選択
          </CustomButton>

          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            onChange={handleChange}
          />

          {file && (
            <div
              style={{
                margin: '2rem auto 0',
                maxWidth: 920,
                textAlign: 'left',
                background: '#fff',
                padding: '1rem 1.25rem',
                borderRadius: 6,
                boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
              }}
            >
              <div style={{ marginBottom: 8, fontWeight: 600 }}>選択中のファイル</div>

              <div style={{ fontSize: 14, lineHeight: 1.8, marginBottom: 8 }}>
                <div>名前：{file.name}</div>
                <div>サイズ：{formatSize(file.size)}</div>
                <div>更新日：{new Date(file.lastModified).toLocaleString()}</div>
                <div>種類：{formatType(file)}</div>
              </div>

              {rowErrors.length > 0 && (
                <div
                  style={{
                    marginBottom: '1rem',
                    background: '#fff',
                    border: '1px solid #fa4d56',
                    borderRadius: 6,
                    color: '#750e13',
                    padding: '0.75rem 1rem',
                  }}
                  aria-live="assertive"
                >
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>
                    データの確認が必要です
                  </div>

                  {rowErrors.slice(0, 5).map((it, i) => (
                    <div key={i}>
                      行 {it.index + 2}：{it.messages[0]}
                      {it.messages.length > 1 ? ` 他${it.messages.length - 1}件` : ''}
                    </div>
                  ))}

                  {rowErrors.length > 5 && (
                    <div>…他 {rowErrors.length - 5} 行</div>
                  )}
                </div>
              )}

              <div style={{ display: 'grid', gap: 16, marginTop: 12 }}>
                {rows.map((r, idx) => {
                  const err = rowErrors.find((e) => e.index === idx);
                  const bad = new Set(err?.fields || []);
                  const title = `レコード ${idx + 1}${err ? '（要確認）' : ''}`;

                  return (
                    <div
                      key={idx}
                      style={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 6,
                        background: '#fff',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          padding: '0.5rem 0.75rem',
                          background: err ? 'rgba(255, 193, 7, 0.15)' : '#f4f4f4',
                          borderBottom: '1px solid #e0e0e0',
                          fontWeight: 600,
                        }}
                      >
                        {title}
                      </div>

                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tbody>
                          {PREVIEW_ORDER.map((k) => {
                            const rule = SCHEMA.find((s) => s.key === k);
                            const isBad = bad.has(k);
                            const v = r[k];
                            const t = typeOf(k);

                            const display =
                              !v && v !== 0 ? '（空）'
                              : t === 'date' ? formatDateLabel(v)
                              : t === 'partialDate' ? formatPartialDateLabel(v)
                              : String(v);

                            return (
                              <tr key={k}>
                                <th
                                  style={{
                                    width: 240,
                                    textAlign: 'left',
                                    padding: '8px 10px',
                                    borderBottom: '1px solid #f2f2f2',
                                    background: '#fafafa',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {labelOf(k)}
                                  {rule?.required ? ' *' : ''}
                                </th>
                                <td
                                  style={{
                                    padding: '8px 10px',
                                    borderBottom: '1px solid #f2f2f2',
                                    background: isBad ? 'rgba(255, 0, 0, 0.06)' : 'transparent',
                                    wordBreak: 'break-word',
                                  }}
                                  title={
                                    isBad ? (err?.messages || []).join('\n') : undefined
                                  }
                                >
                                  {display}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>

                      {err && err.messages?.length > 0 && (
                        <div
                          style={{
                            padding: '8px 10px',
                            color: '#a56e00',
                            background: 'rgba(255, 193, 7, 0.08)',
                          }}
                        >
                          {err.messages.map((m, i) => (
                            <div key={i} style={{ lineHeight: 1.6 }}>・{m}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: 12,
                  marginTop: 16,
                  justifyContent: 'flex-end',
                }}
              >
                <CustomButton kind="ghost" onClick={handleClear}>
                  取り消し
                </CustomButton>
                <CustomButton kind="primary" onClick={handleStartLoad} disabled={!allOk}>
                  登録
                </CustomButton>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/** ▼ API登録payloadを整形してPOST */
async function registerPatient(row) {
  const raw = sessionStorage.getItem("auth");
  const auth = raw ? JSON.parse(raw) : null;

  const payload = {
    doctor_id: auth?.id || "",
    consultation_id: row.consultation_id || "",
    research_id: row.research_id || "",
    pw: row.patient_pw || "",

    date_of_birth: toDate(row.date_of_birth) || "",
    gender_type: row.gender_type || "不明",
    ms_type: row.ms_type || "不明",

    hope_child: row.hope_child || "",
    smoking: row.smoking || "",
    drinking: row.drinking || "",

    complications: row.complications || "",
    medical_history: row.medical_history || "",
    family_history: row.family_history || "",

    first_detect_date: toApiDate(row.first_detect_date) || "",
    first_detect_way: row.first_detect_way || "",
    first_onset_date: toApiDate(row.first_onset_date) || "",
    first_onset_location: row.first_onset_location || "",
    first_onset_symptom: row.first_onset_symptom || "",
    last_onset_date: toApiDate(row.last_onset_date) || "",

    recurrences_times: row.recurrences_times || "",
    pira_presence: row.pira_presence || "",
    pira_suspected_date: toApiDate(row.pira_suspected_date) || "",
    pira_symptoms: row.pira_symptoms || "",

    last_edss3_date: toApiDate(row.last_edss3_date) || "",
    last_edss6_date: toApiDate(row.last_edss6_date) || "",

    aqp4_test: row.aqp4_test || "",
    mog_test: row.mog_test || "",
    antibody_test: row.antibody_test || "",
    cerebrospinal_fluid_test: row.cerebrospinal_fluid_test || "",
    fluid_test_date: toApiDate(row.fluid_test_date) || "",
    ocb_result_type: row.ocb_result_type || "",
    fluid_test_company: row.fluid_test_company || "",

    igg_result_score: Number(row.igg_result_score || 0),
    edss_score: Number(row.edss_score || 0),

    date_of_answer: dayjs().tz("Asia/Tokyo").format("YYYY-MM-DD"),
  };

  try {
    const res = await api.post("/v1/doctor/new_registration", payload);
    return res.data;

  } catch (err) {
    console.error("登録エラー:", err);
    throw err;
  }
}

/** ▼ 医療情報登録（治療薬ごとに複数回呼ぶ） */
async function registerMedicalInfo(row) {
  // 0番目フェーズ：ベース情報だけを先に登録
  const baseConsultationDate = dayjs().tz("Asia/Tokyo").format("YYYY-MM-DD");

  const basePayload = {
    research_id: row.research_id,
    consultation_date: baseConsultationDate,
    edss_score: Number(row.edss_score || 0),
    update_date: dayjs().tz("Asia/Tokyo").format("YYYY-MM-DDTHH:mm:ss.SSS"),
  };

  try {
    console.log(`🔹 Registering base medical info for ${row.research_id}`);
    await api.post("/v1/doctor/register_medical_info", basePayload);

  } catch (err) {
    console.error(`ベース医療情報登録エラー (${row.research_id}):`, err);
    throw err;
  }

  // 治療薬1〜10を走査
  // 1回目は validateRows で必須保証済み。
  // 2回目以降は、薬剤名と開始日が両方空ならスキップ。
  for (let i = 1; i <= 10; i++) {
    const drugName = row[`treatment_drug_${i}`];
    const treatmentStart = row[`treatment_start_${i}`];

    if (i !== 1 && !drugName && !treatmentStart) continue;

    const consultationDate = parseToDate(treatmentStart);

    const payload = {
      research_id: row.research_id,
      consultation_date: consultationDate,
      drug_type: drugName,
      update_date: dayjs().tz("Asia/Tokyo").format("YYYY-MM-DDTHH:mm:ss.SSS"),
    };

    try {
      console.log(`🔹 Registering drug info [${drugName}] for ${row.research_id}`);
      await api.post("/v1/doctor/register_medical_info", payload);

    } catch (err) {
      console.error(`治療薬登録エラー (${drugName}):`, err);
      throw err;
    }
  }
}

const toDate = (s) => {
  if (!s) return "";

  const [y, m, d] = String(s).split("/").map(Number);

  if (!y || !m || !d) return "";

  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
};

function parseToDate(v) {
  if (!v) return "";

  // 区切りを全部 "-" に統一
  const s = String(v).trim().replace(/\//g, "-");

  // 1. 年のみ 例: 2012
  if (/^\d{4}$/.test(s)) {
    return `${s}-01-01`;
  }

  // 2. 年-月 例: 2012-06
  if (/^\d{4}-\d{1,2}$/.test(s)) {
    const [y, m] = s.split("-");
    return `${y}-${m.padStart(2, "0")}-01`;
  }

  // 3. 年-月-日 例: 2012-06-06
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(s)) {
    const [y, m, d] = s.split("-");
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }

  return "";
}

/** ▼ 日付をAPI形式に変換（YYYYMMDD または YYYYMM） */
const toApiDate = (s) => {
  if (!s) return "";

  const v = String(s).trim().replace(/[-\/]/g, "");

  if (/^\d{4}$/.test(v)) return v;
  if (/^\d{6}$/.test(v)) return v;
  if (/^\d{8}$/.test(v)) return v;

  const m = String(s).match(new RegExp("^(\\d{4})[/-](\\d{1,2})(?:[/-](\\d{1,2}))?$"));

  if (m) {
    const y = m[1];
    const mo = m[2].padStart(2, "0");
    const d = m[3] ? m[3].padStart(2, "0") : "";

    return d ? `${y}${mo}${d}` : `${y}${mo}`;
  }

  return "";
};